import type { ReactNode } from 'react';
import type { User } from '@vd/types';
import styles from './renderMentions.module.css';

// Same regex shape the parser uses on the server. Kept verbatim so
// what the parser resolves and what the renderer highlights stay in
// lockstep — drift here would mean a token could turn pink in the UI
// but never show up in the inbox (or vice versa).
const MENTION_RE = /(?<![\w@])@([A-Za-z][\w'\- ]*[\w'-]|[A-Za-z])/g;

/**
 * Walks a comment / description body and returns an array of React
 * nodes with @-mentions wrapped in a styled span. Non-matching segments
 * are returned as plain strings so the caller can keep using
 * white-space: pre-wrap for newline preservation.
 *
 * Resolution mirrors the server: longest member-name prefix wins.
 * Tokens that don't resolve to a member render as plain text — the
 * server wouldn't notify them either, so we shouldn't suggest in the
 * UI that they're a real mention.
 */
export interface RenderMentionsOptions {
  /**
   * Override the className applied to mention spans. The default uses
   * the bold-and-tinted styling from renderMentions.module.css, intended
   * for displayed comments. The textarea highlight overlay passes its
   * own class because the bold/padding/nowrap rules in the default
   * style would shift character positions away from the textarea
   * underneath.
   */
  mentionClassName?: string;
}

export function renderMentions(
  body: string,
  members: Pick<User, 'id' | 'name'>[],
  options?: RenderMentionsOptions,
): ReactNode[] {
  if (!body) return [body];

  // Sort by descending name length so longest prefix wins. Tiebreak by
  // id ASC to match the server-side parser's resolution rule, otherwise
  // a duplicate-named pair could highlight a different user than the
  // one the parser actually picked.
  const indexed = members
    .map((m) => ({ id: m.id, name: m.name, lower: m.name.toLowerCase() }))
    .sort((a, b) => b.lower.length - a.lower.length || a.id.localeCompare(b.id));

  const mentionClass = options?.mentionClassName ?? styles.mention;

  const out: ReactNode[] = [];
  let cursor = 0;
  let key = 0;

  for (const match of body.matchAll(MENTION_RE)) {
    const tokenStart = match.index ?? 0;
    const tokenLower = (match[1] ?? '').toLowerCase();

    // Find the longest member-name prefix that boundary-matches.
    let matched: { name: string; id: string } | null = null;
    for (const member of indexed) {
      if (
        tokenLower === member.lower
        || (tokenLower.startsWith(member.lower)
            && /^[\s.,;:!?]/.test(tokenLower.slice(member.lower.length)))
      ) {
        matched = { name: member.name, id: member.id };
        break;
      }
    }
    if (!matched) continue;

    // Emit any plain text between the previous match and this one.
    if (tokenStart > cursor) out.push(body.slice(cursor, tokenStart));
    // Style the matched portion. The original text is preserved so
    // case the user typed (`@jaSon`) is what's shown — we only wrap
    // it visually.
    const matchedSourceLen = matched.name.length + 1; // +1 for the '@'
    const styledText = body.slice(tokenStart, tokenStart + matchedSourceLen);
    out.push(
      <span key={`m-${key++}`} className={mentionClass} data-user-id={matched.id}>
        {styledText}
      </span>,
    );
    cursor = tokenStart + matchedSourceLen;
  }

  // Trailing tail.
  if (cursor < body.length) out.push(body.slice(cursor));
  return out;
}
